import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Operation Type Selector Component
 * Allows users to choose between Refund and Exchange operations
 * Redirects to appropriate form based on selection
 */
const OperationSelector = ({ preselectedInvoices, preselectedItems, onClose }) => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [selectedOperation, setSelectedOperation] = useState('');

  const handleOperationSelect = (operation) => {
    setSelectedOperation(operation);

    // Navigate to the appropriate form with operation type
    if (invoiceId) {
      navigate(`/refund-exchange/${invoiceId}/${operation}`, {
        state: {
          preselectedInvoices,
          preselectedItems
        }
      });
    }
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="card">
      <div className="card-body add-product pb-0" style={{ padding: '1rem' }}>
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="mb-0">Select Operation Type</h4>
            <p className="text-muted mt-2">
              Choose whether you want to process a refund or exchange for the selected invoice.
            </p>
          </div>
        </div>

        {/* Invoice Information Display */}
        {preselectedInvoices && preselectedInvoices.length > 0 && (
          <div className="row mb-4">
            <div className="col-lg-12">
              <div className="card" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="card-body">
                  <h6 className="card-title">Selected Invoice</h6>
                  <div className="row">
                    <div className="col-md-3">
                      <strong>Order Number:</strong><br />
                      {preselectedInvoices[0].order_number || preselectedInvoices[0].id}
                    </div>
                    <div className="col-md-3">
                      <strong>Customer:</strong><br />
                      {preselectedInvoices[0].billing_name} {preselectedInvoices[0].billing_last_name}
                    </div>
                    <div className="col-md-3">
                      <strong>Total Amount:</strong><br />
                      ${parseFloat(preselectedInvoices[0].total_sale || 0)}
                    </div>
                    <div className="col-md-3">
                      <strong>Status:</strong><br />
                      <span className={`badge ${preselectedInvoices[0].status?.toLowerCase().includes('deliv') ? 'badge-success' : 'badge-warning'}`}>
                        {preselectedInvoices[0].status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Operation Selection Cards */}
        <div className="row">
          <div className="col-lg-6 col-md-6 col-12">
            <div
              className="card operation-card"
              style={{
                cursor: 'pointer',
                border: selectedOperation === 'refund' ? '2px solid #007bff' : '1px solid #dee2e6',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleOperationSelect('refund')}
            >
              <div className="card-body text-center">
                <div style={{ fontSize: '48px', color: '#dc3545', marginBottom: '16px' }}>
                  <i className="fas fa-undo-alt" style={{ color: '#dc3545' }}></i>
                </div>
                <h5 className="card-title">Refund</h5>
                <p className="card-text text-muted">
                  Return money to the customer for returned items.
                </p>
                <ul className="list-unstyled text-left" style={{ fontSize: '14px' }}>
                  <li>✅ Full refund for undelivered items</li>
                  <li>⚠️ 20% fee for delivered items</li>
                  <li>💰 Includes pickup charges</li>
                  <li>🏦 Multiple payment methods</li>
                </ul>
                <button
                  className="btn btn-primary mt-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOperationSelect('refund');
                  }}
                >
                  Select Refund
                </button>
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-md-6 col-12">
            <div
              className="card operation-card"
              style={{
                cursor: 'pointer',
                border: selectedOperation === 'exchange' ? '2px solid #28a745' : '1px solid #dee2e6',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleOperationSelect('exchange')}
            >
              <div className="card-body text-center">
                <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '16px' }}>
                  <i className="fas fa-exchange-alt" style={{ color: '#28a745' }}></i>
                </div>
                <h5 className="card-title">Exchange</h5>
                <p className="card-text text-muted">
                  Replace items with different products.
                </p>
                <ul className="list-unstyled text-left" style={{ fontSize: '14px' }}>
                  <li>🔄 Replace with new items</li>
                  <li>💳 10% exchange fee</li>
                  <li>🚚 Includes pickup charges</li>
                  <li>💰 Store credit calculation</li>
                </ul>
                <button
                  className="btn btn-success mt-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOperationSelect('exchange');
                  }}
                >
                  Select Exchange
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Fee Comparison Table */}
        <div className="row mt-4">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Fee Comparison</h6>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Operation Type</th>
                        <th>Fee Structure</th>
                        <th>Additional Charges</th>
                        <th>Tax Calculation</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Refund (Undelivered)</strong></td>
                        <td className="text-success">0% fee</td>
                        <td>No pickup charges</td>
                        <td>Included in original pricing</td>
                      </tr>
                      <tr>
                        <td><strong>Refund (Delivered)</strong></td>
                        <td className="text-danger">20% fee</td>
                        <td>Pickup charges apply</td>
                        <td>Calculated on net amount</td>
                      </tr>
                      <tr>
                        <td><strong>Exchange</strong></td>
                        <td className="text-warning">10% fee</td>
                        <td>Pickup charges apply</td>
                        <td>On difference amount</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="row mt-4">
          <div className="col-lg-12">
            <div className="btn-addproduct mb-4">
              <button onClick={handleBack} className="btn btn-cancel me-2">
                Back to Invoice
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationSelector;